import './style.css';
import type { BhHooks } from './core/hooks';
import { Game } from './game/game';
import { warmEnvironmentShaders } from './render/renderSystem';
import { holdDownloadsForFirstView, releaseDownloadHold, settleFirstFrames, trackLoads } from './render/shaderWarmup';

declare global {
  interface Window {
    /** Test/debug hook registry — see docs/ARCHITECTURE.md. */
    __bh?: BhHooks;
    __game?: Game;
  }
}

async function boot(): Promise<void> {
  const container = document.getElementById('game-root');
  const uiRoot = document.getElementById('ui-root');
  const loading = document.getElementById('loading');
  if (!container || !uiRoot) throw new Error('index.html is missing #game-root / #ui-root');

  trackLoads();
  // The models wait for the first view's textures (docs/PERF.md "Boot: readiness instead of a fixed wait").
  holdDownloadsForFirstView();
  const params = new URLSearchParams(location.search);
  const seedParam = params.get('seed');
  // `?lockstep`: deterministic playtests — the sim only moves through __bh.stepFrames (see GameOptions).
  const game = new Game({ container, uiRoot, lockstep: params.has('lockstep'), ...(seedParam ? { seed: Number(seedParam) } : {}) });
  // the environment's PMREM shaders compile on the driver's threads while the city is built
  if (game.ctx.render) warmEnvironmentShaders(game.ctx.render.renderer);
  await game.init();
  window.__bh = game.ctx.hooks.api as unknown as BhHooks;
  window.__game = game;
  // Shader pre-warm (docs/PERF.md "Frame pacing and hitches"): the scene's programs compile now, and the
  // first frames (shadow casters, the post passes, the models that land a moment later) draw while the
  // loading screen still covers them.
  performance.mark('bh:prewarm');
  await game.ctx.services.tryGet('renderPipeline')?.prewarm?.(!game.lockstep);
  performance.mark('bh:start');
  game.start();
  if (game.ctx.render && !game.lockstep) await settleFirstFrames(game.ctx.render.renderer);
  performance.mark('bh:playable');
  releaseDownloadHold();
  loading?.classList.add('done');
  setTimeout(() => loading?.remove(), 600);
  game.ctx.render?.canvas.focus();
}

boot().catch((err: unknown) => {
  console.error(err);
  const loading = document.getElementById('loading');
  const label = loading?.querySelector('.bh-load-label');
  if (label) label.textContent = `Failed to start: ${String(err)}`;
  else if (loading) loading.textContent = `Failed to start: ${String(err)}`;
});
